/**
 * Slides for a social-media post, drawn on a canvas in the browser: the photo
 * cropped to the format, the logo (unless the photo has it already), and on the
 * first slide the boat's name, facts and price over a dark band. One JPEG
 * encode per slide.
 */

export type Format = 'post' | 'square' | 'story'

/** Instagram's sizes: feed post 4:5, square 1:1, story / reel 9:16. */
export const FORMATS: Record<Format, { width: number; height: number }> = {
  post: { width: 1080, height: 1350 },
  square: { width: 1080, height: 1080 },
  story: { width: 1080, height: 1920 },
}

export interface CoverText {
  /** "Hallberg-Rassy 342" */
  title: string
  /** "Baujahr 2007 · 10,32 m · Segelyacht" */
  facts: string
  /** "158.000 €", or empty */
  price: string
  /** "Neu im Bestand", "Preis reduziert" … or empty */
  badge: string
}

/** The brand navy (logo, buttons). */
export const BRAND = '#1b2a6b'
export const FONT = 'GillSans'

let fontLoaded: Promise<boolean> | null = null

/**
 * Gill Sans, as the PDFs use it. Each face on its own: Chrome's font sanitizer
 * rejects the regular GillSans.ttf (react-pdf is more lenient), and one bad
 * face must not cost the other. With only the bold face, regular text is set
 * in it too; with neither, the canvas falls back to sans-serif.
 */
export const loadFont = () => {
  fontLoaded ??= (async () => {
    const base = import.meta.env.BASE_URL
    const faces = [
      new FontFace(FONT, `url(${base}fonts/GillSans.ttf)`),
      new FontFace(FONT, `url(${base}fonts/GillSans-Bold.ttf)`, { weight: '700' }),
    ]
    const loaded = await Promise.allSettled(faces.map((face) => face.load()))
    let any = false
    for (const result of loaded) {
      if (result.status === 'fulfilled') {
        document.fonts.add(result.value)
        any = true
      }
    }
    return any
  })()
  return fontLoaded
}

export const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(`could not load ${src}`))
    img.src = src
  })

/** A photo as a bitmap, fetched — so a canvas can be exported afterwards. */
export const fetchBitmap = async (url: string): Promise<ImageBitmap> => {
  const res = await fetch(url)
  if (!res.ok) throw new Error(`${res.status} for ${url}`)
  return createImageBitmap(await res.blob(), { imageOrientation: 'from-image' })
}

const canvas = (width: number, height: number) => {
  const c = document.createElement('canvas')
  c.width = width
  c.height = height
  const ctx = c.getContext('2d')
  if (!ctx) throw new Error('canvas 2d context unavailable')
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  return { c, ctx }
}

/** A rectangle on a photo, as fractions of its width and height. */
export interface Area {
  x: number
  y: number
  w: number
  h: number
}

/** Where a photo goes on a slide, in slide pixels. */
interface Placement {
  x: number
  y: number
  width: number
  height: number
  /** Shown whole, not filling the slide, on a blurred copy of itself. */
  whole: boolean
}

/** How far the crop may move off the centre to keep a logo, as part of the slide. */
const MAX_SHIFT = 0.1

/**
 * Where the photo goes. Normally it fills the slide, cropped around the centre
 * (object-fit: cover). A photo that already carries the logo (`keep`, saved by
 * "Bilder aufbereiten") must not lose part of it: the crop moves a little to
 * keep it whole, and where that would take it far off the centre, the photo is
 * shown whole instead.
 */
export function placement(
  image: { width: number; height: number },
  w: number,
  h: number,
  keep?: Area | null
): Placement {
  const cover = Math.max(w / image.width, h / image.height)
  const width = image.width * cover
  const height = image.height * cover
  const centred = { x: (w - width) / 2, y: (h - height) / 2, width, height, whole: false }
  if (!keep) return centred

  // Along one axis: the offset nearest the centre that keeps the logo on the
  // slide, with a little room to the edge (no more than the photo gave it).
  const pad = w * 0.02
  const along = (frame: number, drawn: number, start: number, length: number) => {
    const from = start * drawn
    const to = (start + length) * drawn
    const lo = Math.max(frame - drawn, Math.min(pad, from) - from)
    const hi = Math.min(0, frame - to - Math.min(pad, drawn - to))
    if (lo > hi) return null
    const offset = Math.min(hi, Math.max(lo, (frame - drawn) / 2))
    return Math.abs(offset - (frame - drawn) / 2) <= frame * MAX_SHIFT ? offset : null
  }
  const x = along(w, width, keep.x, keep.w)
  const y = along(h, height, keep.y, keep.h)
  if (x !== null && y !== null) return { x, y, width, height, whole: false }

  const fit = Math.min(w / image.width, h / image.height)
  const fw = image.width * fit
  const fh = image.height * fit
  return { x: (w - fw) / 2, y: (h - fh) / 2, width: fw, height: fh, whole: true }
}

/** Behind a photo shown whole: the photo itself, filling the slide, blurred and darker. */
function drawBackdrop(
  ctx: CanvasRenderingContext2D,
  image: CanvasImageSource & { width: number; height: number },
  w: number,
  h: number
) {
  // Blurred by scaling down to a few pixels and back up, in two steps: the
  // same in every browser, where canvas filters are not.
  const step = (width: number) => canvas(width, Math.max(1, Math.round((width * h) / w)))
  const tiny = step(24)
  const fill = placement(image, tiny.c.width, tiny.c.height)
  tiny.ctx.drawImage(image, fill.x, fill.y, fill.width, fill.height)
  const soft = step(96)
  soft.ctx.drawImage(tiny.c, 0, 0, soft.c.width, soft.c.height)
  ctx.drawImage(soft.c, 0, 0, w, h)
  ctx.fillStyle = 'rgba(8, 16, 40, 0.3)'
  ctx.fillRect(0, 0, w, h)
}

/** The logo top right, with a soft light glow so it reads on dark water too. */
function drawLogo(
  ctx: CanvasRenderingContext2D,
  logo: CanvasImageSource & { width: number; height: number },
  w: number,
  fraction: number
) {
  const lw = Math.round(w * fraction)
  const lh = Math.round((lw * logo.height) / logo.width)
  const margin = Math.round(w * 0.035)
  ctx.save()
  ctx.shadowColor = 'rgba(255, 255, 255, 0.85)'
  ctx.shadowBlur = Math.max(6, lw * 0.06)
  ctx.drawImage(logo, w - lw - margin, margin, lw, lh)
  ctx.restore()
}

/** The largest font size up to `max` at which `text` fits `width`. */
function fitFont(
  ctx: CanvasRenderingContext2D,
  text: string,
  weight: string,
  max: number,
  width: number
) {
  let size = max
  for (; size > 16; size -= 2) {
    ctx.font = `${weight} ${size}px ${FONT}, "Gill Sans", "Segoe UI", sans-serif`
    if (ctx.measureText(text).width <= width) break
  }
  return size
}

/** The badge top left, or top right when the left is taken (by a logo). */
function drawBadge(ctx: CanvasRenderingContext2D, text: string, w: number, right = false) {
  const pad = Math.round(w * 0.025)
  const size = Math.round(w * 0.04)
  ctx.font = `700 ${size}px ${FONT}, "Gill Sans", sans-serif`
  const tw = ctx.measureText(text.toUpperCase()).width
  const bw = tw + pad * 2
  const x = right ? Math.round(w - w * 0.045 - bw) : Math.round(w * 0.045)
  const y = Math.round(w * 0.045)
  const bh = size + pad * 1.4
  ctx.save()
  ctx.fillStyle = BRAND
  ctx.beginPath()
  ctx.roundRect(x, y, bw, bh, bh / 2)
  ctx.fill()
  ctx.fillStyle = '#ffffff'
  ctx.textBaseline = 'middle'
  ctx.fillText(text.toUpperCase(), x + pad, y + bh / 2 + 1)
  ctx.restore()
}

/** Name, facts and price over a dark band at the bottom. */
function drawCoverText(ctx: CanvasRenderingContext2D, text: CoverText, w: number, h: number) {
  const left = Math.round(w * 0.06)
  const width = w - left * 2
  const band = Math.round(h * (h > w * 1.5 ? 0.3 : 0.38))

  const gradient = ctx.createLinearGradient(0, h - band, 0, h)
  gradient.addColorStop(0, 'rgba(8, 16, 40, 0)')
  gradient.addColorStop(0.45, 'rgba(8, 16, 40, 0.62)')
  gradient.addColorStop(1, 'rgba(8, 16, 40, 0.88)')
  ctx.fillStyle = gradient
  ctx.fillRect(0, h - band, w, band)

  ctx.save()
  ctx.fillStyle = '#ffffff'
  ctx.textBaseline = 'alphabetic'
  ctx.shadowColor = 'rgba(0, 0, 0, 0.35)'
  ctx.shadowBlur = 8

  let y = h - Math.round(h * 0.055)
  if (text.price) {
    const size = fitFont(ctx, text.price, '700', Math.round(w * 0.075), width)
    ctx.fillText(text.price, left, y)
    y -= Math.round(size * 1.15)
  }
  if (text.facts) {
    const size = fitFont(ctx, text.facts, '400', Math.round(w * 0.04), width)
    ctx.globalAlpha = 0.92
    ctx.fillText(text.facts, left, y)
    ctx.globalAlpha = 1
    y -= Math.round(size * 1.35)
  }
  fitFont(ctx, text.title, '700', Math.round(w * 0.07), width)
  ctx.fillText(text.title, left, y)
  ctx.restore()
}

export interface SlideInput {
  image: CanvasImageSource & { width: number; height: number }
  format: Format
  logo: (CanvasImageSource & { width: number; height: number }) | null
  /** Only the first slide carries text. */
  cover?: CoverText
  /** The logo already on the photo, which must stay whole. */
  keep?: Area | null
}

/** One slide, drawn. */
export function drawSlide({ image, format, logo, cover, keep }: SlideInput): HTMLCanvasElement {
  const { width, height } = FORMATS[format]
  const { c, ctx } = canvas(width, height)
  const at = placement(image, width, height, keep)
  if (at.whole) drawBackdrop(ctx, image, width, height)
  ctx.drawImage(image, at.x, at.y, at.width, at.height)
  if (cover) {
    drawCoverText(ctx, cover, width, height)
    // The photo's own logo top left: the badge goes to the other side.
    const left = keep && at.x + (keep.x + keep.w / 2) * at.width < width / 2
    const top = keep && at.y + keep.y * at.height < height * 0.25
    if (cover.badge) drawBadge(ctx, cover.badge, width, Boolean(left && top))
  }
  if (logo) drawLogo(ctx, logo, width, cover ? 0.24 : 0.17)
  return c
}

export const toJpeg = (c: HTMLCanvasElement, quality = 0.9): Promise<Blob> =>
  new Promise((resolve, reject) =>
    c.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('encoding failed'))),
      'image/jpeg',
      quality
    )
  )
